// useTelefonFeld.ts — bindet ein beliebiges <input> an das Telefonformat
// „+49 221 123456-78" (Logik: @kmvs/forms-core, telefon.ts).
//
// Warum ein Hook und nicht nur eine Komponente: Telefonnummern werden in der
// App in sehr verschiedenen Feldern eingegeben (Refined-Formularbausteine,
// die Akte mit ihren „inp"-Feldern, Fluent-`Input`, das Schadenformular …).
// Jede dieser Stellen behält ihr Aussehen und bekommt vom Hook nur die
// Eingabe-Props: Wert, onChange (mit Cursor-Führung), onPaste, onBlur.
//
// Drei Dinge, die der Hook bewusst so macht:
// - Ein gespeicherter Bestandswert („0221 12345") wird für die ANZEIGE
//   formatiert, aber nicht zurückgeschrieben, solange niemand tippt — sonst
//   wäre jedes Formular beim bloßen Öffnen „geändert". Es gibt keine
//   Migration der Bestandsdaten.
// - Nach außen (onChange) geht immer der Speicherwert: „+49 " allein ist leer.
// - Der Cursor wird nach jeder Formatierung dorthin gesetzt, wo er logisch
//   hingehört — kein Sprung ans Ende beim Editieren in der Mitte.

import * as React from "react";
import {
  formatiereTelefon,
  istTelefonFreitext,
  pruefeTelefon,
  telefonEinfuegen,
  telefonEingabe,
  telefonSpeicherwert,
  telefonVerlassen,
  type TelefonErgebnis,
  type TelefonOptionen,
} from "@kmvs/forms-core";
import { aktivesDokument } from "../../windowManager/browserFenster/fensterRegister";

export interface TelefonFeldOptionen {
  value: string | null | undefined;
  onChange?: (wert: string) => void;
  /** Geschäftliche Nummer (Firma / Kommunikationsweg „geschäftlich"): Durchwahl „-" erlaubt. */
  geschaeftlich?: boolean;
  /** Zentralnummer(n) der Firma — für die automatische Durchwahl (nur geschäftlich). */
  zentrale?: TelefonOptionen["zentrale"];
  /** Wird nach dem Verlassen des Feldes aufgerufen (mit dem Speicherwert). */
  onBlur?: (wert: string) => void;
}

export interface TelefonInputProps {
  ref: React.RefObject<HTMLInputElement>;
  value: string;
  type: "tel";
  inputMode: "tel";
  autoComplete: "off";
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => void;
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
}

export interface TelefonFeld {
  /** Auf das <input> spreizen. */
  inputProps: TelefonInputProps;
  /** Angezeigter Feldinhalt. */
  anzeige: string;
  /** Speicherwert der Anzeige. */
  wert: string;
  /** Fehlermeldung zum aktuellen Inhalt (leer ist gültig). */
  fehler?: string;
}

export function useTelefonFeld({ value, onChange, geschaeftlich, zentrale, onBlur }: TelefonFeldOptionen): TelefonFeld {
  const zentraleSchluessel = JSON.stringify(zentrale ?? null);
  const opts = React.useMemo<TelefonOptionen>(
    () => ({ geschaeftlich: !!geschaeftlich, zentrale }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [geschaeftlich, zentraleSchluessel],
  );

  const [anzeige, setAnzeige] = React.useState(() => formatiereTelefon(value, opts));
  const ref = React.useRef<HTMLInputElement>(null);
  const ausNull = React.useRef(false);
  const bearbeitet = React.useRef(false);
  const zielCursor = React.useRef<number | null>(null);
  /** Was wir zuletzt nach außen gegeben haben — ein Echo davon ist kein neuer Wert. */
  const gesendet = React.useRef<string>(String(value ?? ""));
  const anzeigeRef = React.useRef(anzeige);
  anzeigeRef.current = anzeige;

  // Von außen gesetzter Wert (Formular lädt nach, Reset, anderer Datensatz).
  React.useEffect(() => {
    const v = String(value ?? "");
    if (v === gesendet.current) return;
    gesendet.current = v;
    bearbeitet.current = false;
    ausNull.current = false;
    setAnzeige(formatiereTelefon(v, opts));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const cursorSetzen = React.useCallback((pos: number) => {
    const el = ref.current;
    if (!el || aktivesDokument().activeElement !== el) return;
    try {
      el.setSelectionRange(pos, pos);
    } catch {
      /* type=tel unterstützt Selection — nur zur Sicherheit */
    }
  }, []);

  // Nach dem Commit, vor dem Paint: sonst sieht man den Cursor kurz springen.
  React.useLayoutEffect(() => {
    const ziel = zielCursor.current;
    zielCursor.current = null;
    if (ziel != null) cursorSetzen(ziel);
  });

  const senden = (wert: string) => {
    if (wert === gesendet.current) return;
    gesendet.current = wert;
    onChange?.(wert);
  };

  const uebernehmen = (e: TelefonErgebnis) => {
    ausNull.current = e.ausNull;
    bearbeitet.current = true;
    if (e.wert === anzeigeRef.current) {
      // Kein Re-Render (gleicher State) — React setzt den DOM-Wert aber
      // zurück und der Cursor landete am Ende. Also selbst setzen.
      const pos = e.cursor;
      window.setTimeout(() => cursorSetzen(pos), 0);
    } else {
      zielCursor.current = e.cursor;
      setAnzeige(e.wert);
    }
    senden(telefonSpeicherwert(e.wert));
  };

  const inputProps: TelefonInputProps = {
    ref,
    value: anzeige,
    type: "tel",
    inputMode: "tel",
    autoComplete: "off",
    onChange: (e) => {
      const el = e.target;
      const art = (e.nativeEvent as InputEvent | undefined)?.inputType ?? "";
      const ergebnis = telefonEingabe(
        { wert: anzeigeRef.current, ausNull: ausNull.current },
        el.value,
        el.selectionStart ?? el.value.length,
        { ...opts, richtung: art === "deleteContentForward" ? "vor" : "zurueck" },
      );
      if (istTelefonFreitext(ergebnis.wert)) {
        // Bestands-Freitext: unverändert durchreichen.
        bearbeitet.current = true;
        setAnzeige(ergebnis.wert);
        senden(ergebnis.wert);
        return;
      }
      uebernehmen(ergebnis);
    },
    onPaste: (e) => {
      const text = e.clipboardData?.getData("text") ?? "";
      if (!text || istTelefonFreitext(anzeigeRef.current)) return;
      e.preventDefault();
      const el = e.currentTarget;
      const laenge = anzeigeRef.current.length;
      uebernehmen(
        telefonEinfuegen(
          { wert: anzeigeRef.current },
          text,
          { start: el.selectionStart ?? laenge, ende: el.selectionEnd ?? laenge },
          opts,
        ),
      );
    },
    onBlur: () => {
      let wert = String(value ?? "");
      if (bearbeitet.current) {
        const neu = telefonVerlassen(anzeigeRef.current, opts);
        ausNull.current = false;
        setAnzeige(neu);
        senden(neu);
        wert = neu;
      }
      onBlur?.(wert);
    },
  };

  const wert = telefonSpeicherwert(anzeige);
  return { inputProps, anzeige, wert, fehler: pruefeTelefon(wert) };
}
